import Autocomplete from '@components/v2/form/Autocomplete/Autocomplete';
import { ControllerRenderProps } from 'react-hook-form';
import { useListPick } from '../../../components/ListPicker/useListPick';
import LangEnum from '../../../lang/LangEnum';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import { onScrollEndReload } from '../../../utils/generics';
import { CatalogPriceTable } from '../../catalogs/components/CatalogExportModal/components/PriceTableAutocomplete/PriceTableAutocomplete';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../translation/TranslationContext';
import { ISharedEntityForm } from '../types/sharedEntities.types';

interface IPriceTableAutocomplete {
  field: ControllerRenderProps<ISharedEntityForm, 'config.priceTableId'>;
  entityId?: string;
}

const PriceTableAutocomplete = ({ entityId, field }: IPriceTableAutocomplete) => {
  const { translate } = useTranslation();
  const { isAdminBrand, brandId } = useApplicationContext();
  const selectedOrder = useOrderBookStore((orderBookState) => orderBookState.selectedOrder);

  const url = isAdminBrand
    ? 'price-tables'
    : `sales-representatives/export-catalog/available-price-tables`;

  const listPickActions = useListPick<Partial<CatalogPriceTable>>({
    url,
    queryField: 'nameOrCode',
    limit: 30,
    defaultQuery: {
      brandId,
      active: true,
      orderId: isAdminBrand ? undefined : selectedOrder?.id,
      customerId: isAdminBrand ? undefined : selectedOrder?.customer?.id,
    },
    shouldFetch: true,
    order: {
      code: 'ASC',
      defaultTable: 'ASC',
    },
    disabled: !entityId,
  });

  const priceTables = listPickActions.leftItems;

  const selectedPriceTable =
    priceTables.find((priceTable) => (priceTable.id ?? priceTable.priceTableId) === field.value) ??
    null;

  return (
    <Autocomplete<Partial<CatalogPriceTable>>
      options={priceTables}
      value={selectedPriceTable}
      loading={listPickActions.loading}
      getOptionLabel={(option) => option.name ?? ''}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      noOptionsText={translate(LangEnum.GLOBAL_NO_OPTIONS)}
      loadingText={translate(LangEnum.GLOBAL_SEARCHING)}
      textFieldProps={{ label: translate(LangEnum.CATALOGS_EXPORT_PRICE_TABLE_LABEL) }}
      slotProps={{
        listbox: {
          style: { maxHeight: 248 },
          onScroll: (event: React.SyntheticEvent) => onScrollEndReload(event, listPickActions),
        },
      }}
      onInputChange={(_, query, reason) => {
        if (reason !== 'input') {
          return;
        }

        listPickActions.onSearch(query);
      }}
      onChange={(_, priceTable) => field.onChange(priceTable?.id ?? priceTable?.priceTableId)}
    />
  );
};

export default PriceTableAutocomplete;
